import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";

import { useLocalizedHref } from "@/libraries/web-framework";

import { CtaButton } from "./components/cta-button";

export type headerProps = {
    readonly mainHeading: string;
    readonly subHeading: string;
    readonly buttonText: string;
};

const sectionRootStyles = css({
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start"
});

const sectionFramingStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: "100%",
    justifyContent: "center",
    boxSizing: "border-box",
    marginBlockStart: "64px",
    [media.below.lg]: {
        marginBlockStart: "32px",
        width: "90%"
    }
});

const headerStyles = css({
    isolation: "isolate",
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontSize: "66px",
    fontWeight: "400",
    lineHeight: "73px",
    textAlign: "center",
    color: "#101214",
    marginBlock: "0px",

    // Large tablets
    [media.only.md]: {
        fontSize: "36px",
        letterSpacing: "0"
    },

    // Small tablets
    [media.only.sm]: {
        fontSize: "36px",
        letterSpacing: "0"
    },

    // Phones
    [media.below.sm]: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        fontSize: "36px",
        letterSpacing: "0"
    }
});

const highlightedTextStyles = css({
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontSize: "66px",
    fontWeight: "800",
    lineHeight: "73px",
    textAlign: "center",
    position: "relative",
    ["&:before"]: {
        content: " ",
        background:
            'url("https://wac-cdn.atlassian.com/misc-assets/webp-images/header-line.webp")',
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        position: "absolute",
        insetInlineStart: "-2px",
        insetBlockEnd: "18px",
        width: "105%",
        height: "44px",
        display: "inline-block",
        transform: "rotate(-1.434deg)",
        mixBlendMode: "multiply",
        zIndex: "-1"
    },
    [media.only.md]: {
        fontSize: "36px",
        letterSpacing: "0",
        ["&:before"]: {
            backgroundSize: "100%",
            insetBlockEnd: "8px",
            height: "23px"
        }
    },
    [media.only.sm]: {
        fontSize: "36px",
        letterSpacing: "0",
        ["&:before"]: {
            backgroundSize: "100%",
            insetBlockEnd: "8px",
            height: "23px"
        }
    },
    [media.below.sm]: {
        lineHeight: "10px",
        fontSize: "36px",
        letterSpacing: "0",
        ["&:before"]: {
            backgroundSize: "100%",
            insetBlockEnd: "-15px",
            height: "23px"
        }
    }
});

const subheadingStyles = css({
    fontFamily: "var(--bxp-charlie-text-font-family)",
    fontStyle: "normal",
    fontWeight: "400",
    fontSize: "20px",
    lineHeight: "28px",
    textAlign: "center",
    letterSpacing: "0",
    color: "#101214",
    marginBlockStart: "28px",
    [media.only.md]: {
        marginBlockStart: "12px",
        fontSize: "16px",
        lineHeight: "26px",
        boxSizing: "border-box"
    },
    [media.only.sm]: {
        marginBlockStart: "12px",
        fontSize: "16px",
        lineHeight: "26px",
        boxSizing: "border-box"
    },
    [media.below.sm]: {
        marginBlockStart: "36px",
        fontSize: "16px",
        lineHeight: "26px",
        boxSizing: "border-box"
    }
});

const ctaContainer = css({
    marginBlockStart: "44px",
    [media.below.sm]: {
        marginBlockStart: "40px"
    }
});

const Header = ({ mainHeading, subHeading, buttonText }: headerProps) => {
    const splitIndex = mainHeading.indexOf("start");
    const headingFirstPart = mainHeading.substring(0, splitIndex);
    const headingSecondPart = mainHeading.substring(splitIndex);

    return (
        <div css={sectionRootStyles}>
            <div css={sectionFramingStyles}>
                <h1 css={headerStyles}>
                    <span>{headingFirstPart}</span>
                    <span css={highlightedTextStyles}>{headingSecondPart}</span>
                </h1>
                <div css={subheadingStyles}>{subHeading}</div>
                <div css={ctaContainer}>
                    <CtaButton
                        analyticsEventLabel="atlas-55799-header-cta-button-clicked"
                        text={buttonText}
                        ctaButtonUrl={useLocalizedHref(
                            "/try/cloud/signup?bundle=jira-software"
                        )}
                        bgColor="#0052CC"
                        textColor="#FFFFFF"
                    />
                </div>
            </div>
        </div>
    );
};

export { Header };
